import type { ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { LanguageSwitcher, useI18n } from "../i18n/I18n";

const NAVIGATION = [
  { to: "/folders", labelKey: "app.nav.folders", icon: "--icon-folder" },
  { to: "/teams", labelKey: "app.nav.teams", icon: "--icon-users" },
  { to: "/users", labelKey: "app.nav.users", icon: "--icon-user" },
] as const;

/**
 * Header and page frame, as the SDS console shell (`ds-app`).
 *
 * `ds-app` fills the viewport and scrolls its main area rather than the page,
 * which is what keeps the header in place without a sticky rule of our own.
 *
 * The identity block is the reason this exists rather than a bare `<main>`:
 * every screen shows contracts for one organisation, and who is signed in is
 * what decides which organisation that is.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const { t } = useI18n();

  return (
    <div className="ds-app">
      <header className="ds-app__header ds-header">
        <Link to="/">
          <span className="ds-logo" />
          <span className="ds-text --medium-bold">{t("app.title")}</span>
        </Link>
        <aside className="__controls">
          {auth.isSignedIn && (
            <nav className="app-nav" aria-label={t("app.navigation")}>
              {NAVIGATION.map(({ to, labelKey, icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    `ds-button --plain --small ${icon}${isActive ? " --active" : ""}`
                  }
                >
                  <span>{t(labelKey)}</span>
                </NavLink>
              ))}
            </nav>
          )}
          {auth.isSignedIn && auth.email !== undefined && (
            <span className="ds-text --small app-identity" title={auth.email}>
              {auth.email}
            </span>
          )}
          {auth.isSignedIn && auth.canSignOut && (
            <button
              type="button"
              className="ds-button --plain --small --icon-signout"
              onClick={() => auth.signOut()}
            >
              <span>{t("app.signOut")}</span>
            </button>
          )}
          <LanguageSwitcher />
          <ds-theme />
        </aside>
      </header>
      <article className="ds-app__main">
        <main className="ds-app__main-content app-content">{children}</main>
      </article>
    </div>
  );
}
